import { ApolloClient } from '@apollo/client';
import { useApolloClient, useQuery } from '@apollo/client/react';
import { useCallback, useEffect, useRef } from 'react';
import { toast } from 'react-toastify';

import { Dictionary } from '@calo/types';

import { useQueryErrorToast } from './useQueryErrorToast';
import { fetchAllPages } from './utils';
import { Kitchen, ListFoodComponentByRangeQuery, ListFoodComponentByRangeQueryVariables } from '../libs';
import { LIST_FOOD_COMPONENT_BY_RANGE_QUERY } from '../libs/appGraphQL';

interface UseListComponentByRangeQueryProps {
  kitchen: string;
  startDate: string;
  endDate: string;
  limit?: number;
  filters?: Dictionary<boolean>;
  fetchAll?: boolean;
}

type ComponentRange = Omit<UseListComponentByRangeQueryProps, 'fetchAll'>;

const toVariables = ({ kitchen, startDate, endDate, limit = 450, filters }: ComponentRange) => ({
  kitchen: kitchen as Kitchen,
  startDate,
  endDate,
  limit,
  filters
});

/** Replaces the cached range with every page from the API; `onlyIfLoaded` skips a range no screen has fetched yet. */
export const refetchAllComponentPages = async (
  client: ApolloClient,
  range: ComponentRange,
  { onlyIfLoaded = false }: { onlyIfLoaded?: boolean } = {}
) => {
  const variables = toVariables(range);
  if (onlyIfLoaded && !client.readQuery({ query: LIST_FOOD_COMPONENT_BY_RANGE_QUERY, variables })) return;

  try {
    const components = await fetchAllPages(async (next) => {
      const res = await client.query<ListFoodComponentByRangeQuery, ListFoodComponentByRangeQueryVariables>({
        query: LIST_FOOD_COMPONENT_BY_RANGE_QUERY,
        variables: { ...variables, next },
        fetchPolicy: 'no-cache'
      });
      const payload = res.data?.listFoodComponentByRange;
      return { items: payload?.components ?? [], next: payload?.next };
    });

    client.writeQuery<ListFoodComponentByRangeQuery, ListFoodComponentByRangeQueryVariables>({
      query: LIST_FOOD_COMPONENT_BY_RANGE_QUERY,
      variables,
      data: {
        listFoodComponentByRange: { __typename: 'ListFoodComponentByRangeResponse', components, next: null }
      } as unknown as ListFoodComponentByRangeQuery
    });
  } catch (error) {
    toast.error(error instanceof Error ? error.message : 'Failed to refresh components');
  }
};

const useListComponentByRangeQuery = ({
  kitchen,
  startDate,
  endDate,
  limit = 450,
  filters,
  fetchAll = false
}: UseListComponentByRangeQueryProps) => {
  const filtersKey = JSON.stringify(filters);
  const client = useApolloClient();

  const { data, error, loading, fetchMore, refetch } = useQuery<
    ListFoodComponentByRangeQuery,
    ListFoodComponentByRangeQueryVariables
  >(LIST_FOOD_COMPONENT_BY_RANGE_QUERY, {
    variables: toVariables({ kitchen, startDate, endDate, limit, filters }),
    notifyOnNetworkStatusChange: true
  });

  useQueryErrorToast(error);

  const foodComponents = data?.listFoodComponentByRange?.components ?? [];
  const nextToken = data?.listFoodComponentByRange?.next ?? undefined;

  const seen = useRef(new Set<string>());
  useEffect(() => {
    if (!fetchAll || !nextToken) {
      return;
    }
    if (seen.current.has(nextToken)) {
      return;
    }
    seen.current.add(nextToken);
    void fetchMore({ variables: { next: nextToken } });
  }, [fetchAll, nextToken, kitchen, startDate, endDate]);

  useEffect(() => {
    seen.current.clear();
  }, [kitchen, startDate, endDate, filtersKey]);

  const handleFetchMore = () => {
    if (!nextToken) return;
    void fetchMore({
      variables: { ...toVariables({ kitchen, startDate, endDate, limit, filters }), next: nextToken }
    });
  };

  const refetchAllPages = useCallback(async () => {
    await refetchAllComponentPages(client, { kitchen, startDate, endDate, limit, filters });
    seen.current.clear();
  }, [client, kitchen, startDate, endDate, limit, filtersKey]);

  return {
    componentsLoading: loading,
    foodComponents,
    refetchComponents: fetchAll ? refetchAllPages : refetch,
    hasNext: !!nextToken,
    next: nextToken,
    handleFetchMoreComponents: handleFetchMore
  };
};

export default useListComponentByRangeQuery;
